import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock } from 'lucide-react';
import api from '../../lib/api';
import { PlanExplanation } from '../PlanExplanation';

export default function MyPlanTab({ me, onDataChange, onUpgrade }) {
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkin, setCheckin] = useState({ monthly_income: '', current_savings: '', current_debt: '' });
  const [submittingCheckin, setSubmittingCheckin] = useState(false);

  const fetchPlan = useCallback(() => {
    api.get('/my-plan')
      .then(({ data }) => { setPlanData(data); setNotFound(false); })
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const toggleStep = async (step, done) => {
    setPlanData((prev) => {
      const progress = { ...prev.progress, [`step${step}_done`]: done };
      const stepsDone = [1, 2, 3].filter((n) => progress[`step${n}_done`]).length;
      return { ...prev, progress: { ...progress, steps_done: stepsDone, percent: Math.round((stepsDone / 3) * 100) } };
    });
    try {
      const { data } = await api.patch('/my-plan/progress', { step, done });
      setPlanData((prev) => ({ ...prev, progress: data.progress }));
      onDataChange();
    } catch {
      toast.error('Could not update progress');
      fetchPlan();
    }
  };

  const submitCheckin = async (e) => {
    e.preventDefault();
    setSubmittingCheckin(true);
    try {
      await api.post('/checkin', {
        monthly_income: parseFloat(checkin.monthly_income),
        current_savings: parseFloat(checkin.current_savings),
        current_debt: parseFloat(checkin.current_debt),
      });
      toast.success('Check-in complete — your score has been updated');
      setCheckinOpen(false);
      fetchPlan();
      onDataChange();
    } catch {
      toast.error('Check-in failed. Please try again.');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  if (notFound) {
    return (
      <div data-testid="myplan-empty" className="bg-white border border-[#E5E5EB] rounded-2xl p-10 text-center">
        <h3 className="text-xl font-semibold text-[#0A0A0B] mb-2">No plan yet</h3>
        <p className="text-sm text-[#5C5C66] mb-6">Answer five quick questions and get your personalised 3-step Malaysian action plan.</p>
        <button
          data-testid="myplan-create-btn"
          onClick={() => navigate('/app')}
          className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-8 py-4 font-semibold"
        >
          Create My Plan
        </button>
      </div>
    );
  }

  if (!planData) return <div className="text-[#5C5C66] text-sm p-4">Loading your plan...</div>;

  const { plan, progress } = planData;
  const steps = [1, 2, 3].map((n) => ({
    n,
    title: plan[`step${n}_title`],
    amount: plan[`step${n}_amount`],
    explanation: plan[`step${n}_explanation`],
    done: progress[`step${n}_done`],
  }));

  return (
    <div className="space-y-6">
      {me?.needs_checkin && !checkinOpen && (
        <div data-testid="checkin-banner" className="bg-[#534AB7] text-white rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <CalendarClock size={22} className="mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold mb-0.5">Time for your monthly check-in</h4>
              <p className="text-sm text-white/80">It's been over 30 days. Update your numbers to keep your score accurate.</p>
            </div>
          </div>
          <button
            data-testid="checkin-open-btn"
            onClick={() => {
              setCheckin({
                monthly_income: String(planData.inputs.monthly_income),
                current_savings: String(planData.inputs.current_savings),
                current_debt: String(planData.inputs.current_debt),
              });
              setCheckinOpen(true);
            }}
            className="bg-white text-[#534AB7] hover:bg-white/90 rounded-xl px-5 py-2.5 font-semibold text-sm whitespace-nowrap"
          >
            Update Now
          </button>
        </div>
      )}

      {checkinOpen && (
        <form data-testid="checkin-form" onSubmit={submitCheckin} className="bg-white border border-[#534AB7]/30 rounded-2xl p-6 space-y-4">
          <h4 className="font-semibold text-[#0A0A0B]">Monthly Check-in</h4>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { key: 'monthly_income', label: 'Monthly Income (RM)' },
              { key: 'current_savings', label: 'Current Savings (RM)' },
              { key: 'current_debt', label: 'Current Debt (RM)' },
            ].map((f) => (
              <div key={f.key}>
                <label className="block mb-1.5 text-xs font-semibold uppercase tracking-wider text-[#5C5C66]">{f.label}</label>
                <input
                  data-testid={`checkin-${f.key}`}
                  type="number"
                  required
                  min="0"
                  step="any"
                  value={checkin[f.key]}
                  onChange={(e) => setCheckin({ ...checkin, [f.key]: e.target.value })}
                  className="w-full bg-white border border-[#E5E5EB] rounded-xl px-4 py-3 text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#534AB7]"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button data-testid="checkin-submit-btn" type="submit" disabled={submittingCheckin} className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-6 py-3 font-semibold text-sm disabled:opacity-50">
              {submittingCheckin ? 'Saving...' : 'Save Check-in'}
            </button>
            <button type="button" onClick={() => setCheckinOpen(false)} className="text-[#5C5C66] hover:text-[#0A0A0B] px-4 py-3 font-medium text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-[#E5E5EB] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-[#0A0A0B]">Your 3-Step Action Plan</h3>
          <span data-testid="plan-progress-label" className="text-sm font-semibold text-[#534AB7]">{progress.steps_done}/3 done</span>
        </div>
        <div className="h-2.5 bg-[#F0F0F4] rounded-full overflow-hidden" data-testid="plan-progress-bar">
          <div className="h-full bg-[#10B981] rounded-full" style={{ width: `${progress.percent}%`, transition: 'width 0.5s ease' }} />
        </div>
      </div>

      {steps.map((step) => (
        <div
          key={step.n}
          data-testid={`plan-step-${step.n}`}
          className={`bg-white border rounded-2xl p-6 sm:p-8 transition-colors ${step.done ? 'border-[#10B981]/40 bg-[#10B981]/[0.03]' : 'border-[#E5E5EB]'}`}
        >
          <div className="flex items-start gap-4">
            <label className="flex-shrink-0 mt-1 cursor-pointer">
              <input
                data-testid={`step-${step.n}-checkbox`}
                type="checkbox"
                checked={step.done}
                onChange={(e) => toggleStep(step.n, e.target.checked)}
                className="w-6 h-6 rounded-md accent-[#10B981] cursor-pointer"
              />
            </label>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Step {step.n}</span>
                {step.done && <span className="text-xs font-semibold text-[#10B981]">Done</span>}
              </div>
              <h4 className={`text-lg sm:text-xl font-semibold mb-1 ${step.done ? 'text-[#5C5C66] line-through' : 'text-[#0A0A0B]'}`}>{step.title}</h4>
              <p className="text-xl font-bold text-[#534AB7] mb-3">{step.amount}</p>
              <PlanExplanation text={step.explanation} className="text-sm sm:text-base text-[#5C5C66] leading-relaxed" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
